import React, { useEffect, useMemo, useState } from "react";
import { deleteHealthCenter } from "../../pages/DoctorManagement/HealthCenters/HealthCenterServices";
import { MaterialReactTable } from 'material-react-table';
import { MenuItem, IconButton, Chip } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";

const HealthCentersTable = ({ 
    data, 
    onSelectionChange, 
    rowSelection, 
    setRowSelection, 
    onCenterDeleted, 
    onViewDetails 
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    
    const memoizedData = useMemo(() => data || [], [data]);

    useEffect(() => {
        const selected = Object.keys(rowSelection)
            .map((key) => memoizedData.find((item) => item._id === key))
            .filter(Boolean);
        onSelectionChange?.(selected);
    }, [rowSelection, memoizedData, onSelectionChange]);

    // Function to handle deletion
    const handleDelete = async (center) => {
        const confirmDelete = window.confirm(`¿Estás seguro de que quieres eliminar "${center.name}"?`);
        if (!confirmDelete) return;

        try {
            setIsLoading(true);
            await deleteHealthCenter(center._id);
            alert("✅ Centro eliminado correctamente");
            onCenterDeleted?.();
        } catch (error) {
            console.error("Error al eliminar:", error);
            alert("❌ Error al eliminar el centro");
        } finally {
            setIsLoading(false);
        }
    };

    // Function to render services as chips
    const renderServices = (services) => {
        if (!services || !Array.isArray(services) || services.length === 0) {
            return <span>-</span>;
        }

        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {services.slice(0, 3).map((service, index) => (
                    <Chip 
                        key={index}
                        label={service} 
                        size="small"
                        variant="outlined"
                        color="primary"
                    />
                ))}
                {services.length > 3 && (
                    <Chip 
                        label={`+${services.length - 3} más`} 
                        size="small"
                        variant="outlined"
                        color="secondary"
                    />
                )}
            </div>
        );
    };

    // Column definitions
    const columns = useMemo(() => [
        
        {
            accessorKey: '_id',
            header: 'ID',
            size: 80,
            Cell: ({ cell }) => (
                <span style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                    {cell.getValue()}
                </span>
            ),
        },
        {
            accessorKey: 'name',
            header: 'Name',
            size: 200,
            Cell: ({ cell }) => (
                <strong style={{ color: '#000000ff' }}>
                    {cell.getValue()}
                </strong>
            ),
        },
        {
            accessorKey: 'phone',
            header: 'Phone',
            size: 150,
        },
        {
            accessorKey: 'email',
            header: 'Email',
            size: 200,
            Cell: ({ cell }) => (
                <span style={{ color: '#666' }}>
                    {cell.getValue()}
                </span>
            ),
        },
        {
            accessorKey: 'address',
            header: 'Address',
            size: 250,
        },
        {
            accessorKey: 'services',
            header: 'Services',
            size: 300,
            enableSorting: false,
            enableColumnFilter: false,
            Cell: ({ cell }) => renderServices(cell.getValue()),
        },
    ], []);

    return (
        <div >
            <MaterialReactTable
                columns={columns}
                data={memoizedData}
                state={{
                    isLoading,
                    rowSelection,
                    globalFilter,
                }}
                // Row selection
                enableRowSelection
                getRowId={(row) => row._id}
                onRowSelectionChange={setRowSelection}
                
                // Filtering and search
                enableColumnFilters
                enableGlobalFilter
                onGlobalFilterChange={setGlobalFilter}
                
                // Sorting and ordering
                enableSorting
                enableColumnOrdering
                
                // Pagination
                enablePagination
                initialState={{
                    density: 'compact',
                    pagination: { pageIndex: 0, pageSize: 10 },
                    columnOrder: [
                        'mrt-row-select',
                        'mrt-row-actions',
                        '_id',
                        'name',
                        'phone',
                        'email',
                        'address',
                        'services'
                    ],
                }}
                
                // Row actions menu
                enableRowActions
                positionActionsColumn="first"
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <MenuItem
                        key="details"
                        sx={{ fontSize: 15, fontWeight: 500, gap: 1.5, my: 0.5 }}
                        onClick={() => {
                            closeMenu();
                            onViewDetails?.(row.original);
                        }}
                    >
                        <i className="bi bi-file-earmark-plus"></i>
                        Details
                    </MenuItem>,
                    <MenuItem
                        key="members"
                        sx={{ fontSize: 15, fontWeight: 500, gap: 1.5, my: 0.5 }}
                        onClick={() => {
                            closeMenu();
                            console.log("Members", row.original);
                        }}
                    >
                        <i className="bi bi-person"></i>
                        Members
                    </MenuItem>,
                    <MenuItem
                        key="patientBase"
                        sx={{ fontSize: 15, fontWeight: 500, gap: 1.5, my: 0.5 }}
                        onClick={() => {
                            closeMenu();
                            console.log("Patient Base", row.original);
                        }}
                    >
                        <i className="bi bi-person-gear"></i>
                        Patient Base
                    </MenuItem>,
                    <MenuItem
                        key="schedule"
                        sx={{ fontSize: 15, fontWeight: 500, gap: 1.5, my: 0.5 }}
                        onClick={() => {
                            closeMenu();
                            console.log("Check Schedule", row.original);
                        }}
                    >
                        <i className="bi bi-calendar-event"></i>
                        Check Schedule
                    </MenuItem>,
                    <MenuItem
                        key="delete"
                        sx={{ 
                            fontSize: 15, 
                            fontWeight: 500, 
                            gap: 1.5, 
                            my: 0.5,
                            color: 'error.main'
                        }}
                        onClick={() => {
                            closeMenu();
                            handleDelete(row.original);
                        }}
                    >
                        <i className="bi bi-trash"></i>
                        Delete
                    </MenuItem>
                ]}
                
                // Custom actions button with vertical dots
                displayColumnDefOptions={{
                    'mrt-row-actions': {
                        muiTableHeadCellProps: {
                            align: 'center',
                        },
                        muiTableBodyCellProps: {
                            align: 'center',
                        },
                    },
                }}
                
                // Custom styling with horizontal lines and white background
                muiTableProps={{
                    sx: {
                        backgroundColor: '#ffffff',
                        '& .MuiTable-root': {
                            backgroundColor: '#ffffff',
                            
                        },
                    },
                }}
                muiTableBodyRowProps={({ row }) => ({
                    sx: {
                        backgroundColor: '#ffffff',
                        borderBottom: '1px solid #e0e0e0',
                        '&:hover': {
                            backgroundColor: '#f5f5f5',
                        },
                        '&:last-child': {
                            borderBottom: '1px solid #e0e0e0',
                        },
                    },
                })}
                muiTableHeadCellProps={{
                    sx: {
                        backgroundColor: '#ffffff',
                        fontWeight: 'bold',
                        borderBottom: '1px solid #d0d0d0',
                    },
                }}
                muiTableBodyCellProps={{
                    sx: {
                        backgroundColor: '#ffffff',
                        borderBottom: '1px solid #d0d0d0',
                    },
                }}
                muiTableContainerProps={{
                    sx: {
                        backgroundColor: '#ffffff',
                    },
                }}
                
                // Pagination customization
                muiPaginationProps={{
                    rowsPerPageOptions: [5, 10, 25, 50],
                    showFirstButton: true,
                    showLastButton: true,
                    sx: {
                        marginTop: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        backgroundColor: '#ffffff',
                        '& .MuiPaginationItem-root': {
                            fontWeight: 'bold',
                            '&.Mui-selected': {
                                backgroundColor: '#007bff',
                                borderRadius: '50%',
                                color: '#fff',
                            },
                            '&:hover': {
                                backgroundColor: '#007bff22',
                            },
                        },
                    },
                }}
                
                // Toolbar customization
                muiTopToolbarProps={{
                    sx: {
                        backgroundColor: '#ffffff',
                    },
                }}
                muiBottomToolbarProps={{
                    sx: {
                        backgroundColor: '#ffffff',
                        borderTop: '1px solid #e0e0e0',
                    },
                }}
                
                // Search box customization
                muiSearchTextFieldProps={{
                    placeholder: 'Search health centers...',
                    sx: { 
                        minWidth: '300px',
                        backgroundColor: '#ffffff',
                    },
                    variant: 'outlined',
                }}
            />
        </div>
    );
};

export default HealthCentersTable;